import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classNames from "classnames";
import { produce } from "immer";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as LinkedInIcon } from '../../../assets/icons/linkedin.svg';
import { DashboardContext } from "../../../contexts/DashboardContext";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { WorkspacesContext } from '../../../contexts/WorkspacesContext';
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';
import useRoute from '../../../hooks/useRoute';
import { getDashboardQueriesFunc } from "../../../lib/helper";
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from "../../../theme";
import { DashboardQuery, DashboardQuerySource, DealSource } from "../../../types/files";
import MultiSelector from '../../atoms/MultiSelector';
import { FallbackLoading } from '../../templates/loader';

export const useStyles = makeStyles((theme) => ({
    card: {
        minWidth: 350,
        maxWidth: 350,
        width: 'auto',
        minHeight: 270,
        maxHeight: 400,
        borderRadius: 16,
        height: 'fit-content',
        overflow: 'hidden',
        background: '#fff',
        border: `1px solid ${theme.colors.neutral['200']}`,
        boxShadow: '0px 2px 8px 0px rgba(16, 24, 40,0.10)',
        transition: 'all 300ms ease',
        "&:hover": {
            boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        },
    },
    dealSource: {
        width: '100%',
        height: 30,
        background: theme.colors.blue['100'],
        paddingLeft: 16,
    },
    keyPeople: {
        width: '100%',
        height: 30,
        background: theme.colors.warning['100'],
        paddingLeft: 16,
    },
    container: {
        padding: '12px 24px 8px 16px',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['600'],
        maxWidth: '24ch',
        textOverflow: 'ellipsis',
    },
    link: {
        display: 'inline-block',
        fontFamily: 'Inter !important',
        fontSize: '1rem',
        fontWeight: 'bold',
        textDecoration: 'none',
        color: theme.palette.primary.main,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
    },
    email: {
        display: 'inline-block',
        fontFamily: 'Inter !important',
        fontSize: '0.95rem',
        textDecoration: 'none',
        color: theme.colors.neutral['500'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
    },
    answer: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        color: '#666666',
        textTransform: 'none !important' as any,
        whiteSpace: 'pre-line',
    },
    collapsableText: {
        width: '100%',
        padding: '4px 0',
        cursor: 'text',
        transition: 'all 300ms ease',
    },
    textClamp: {
        color: '#666666',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    label: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    icon: {
        width: 28,
        height: 28,
        fill: theme.colors.neutral['400'],
    },
    url: {
        display: 'inline-block',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 500,
        textDecoration: 'none',
        color: theme.colors.neutral['600'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
        "&:hover": {
            textDecoration: 'none !important',
        },
    },
    source: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    sublabel: {
        fontWeight: '400',
        color: theme.colors.neutral['500'],
    },
}));

const DealSourceCard: React.FC<{ source?: DealSource }> = ({ source }) => {
    const classes = useStyles();
    const { workspaces } = useContext(WorkspacesContext);
    const { redirectToWorkspace } = useRoute();

    const workspace = useMemo(() => workspaces.find(ws =>
        ws?.name?.toLowerCase() === source?.name?.toLowerCase()
        && ws?.email?.toLowerCase() === source?.email?.toLowerCase()
    ), [source, workspaces]);

    return (<>
        <Stack className={classes.card}>
            <Stack className={classes.dealSource} justifyContent="center">
                <Typography fontFamily="Inter" fontSize="0.8rem" fontWeight={700}
                    color={theme.colors.blue['600']}>
                    {'Deal Source'}
                </Typography>
            </Stack>
            <Stack className={classes.container}>
                {!!source?.name && (
                    <Typography className={classes.source}
                        onClick={() => !!workspace ? redirectToWorkspace(workspace?.id) : undefined}
                        sx={{ cursor: !!workspace ? 'pointer' : 'auto'}}>
                        {source?.name}
                    </Typography>
                )}
                {!!source?.email && (
                    <Typography className={classNames(classes.source, classes.sublabel)}>
                        {source?.email}
                    </Typography>
                )}
            </Stack>
            <Stack alignItems="center" justifyContent="center" height={120}>
                <Typography color={theme.colors.neutral['500']} fontSize="0.95rem" fontWeight={500}>
                    {'No description yet.'}
                </Typography>
            </Stack>
        </Stack>
    </>);
};

const PersonCard: React.FC<{ element: any }> = ({ element }) => {
    const classes = useStyles();
    const [isExpanded, setExpanded] = useState<boolean>(false);
    const textRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (textRef.current)
            textRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }, [isExpanded]);

    return (<>
        <Stack className={classes.card}>
            <Stack className={classes.keyPeople} justifyContent="center">
                <Typography fontFamily="Inter" fontSize="0.8rem" fontWeight={700}
                    color={theme.colors.orange['600']}>
                    {'Team'}
                </Typography>
            </Stack>
            <Stack className={classes.container}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" minWidth="80%"
                        sx={{ cursor: !!element.link ? 'pointer' : 'auto' }}
                        onClick={!!element.link ? (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            window.open(element.link, '_blank', 'noopener, noreferrer');
                        } : undefined}>
                        <Typography className={classes.link}>
                            {element.title.split('|')?.[0]?.split('-')?.[0]}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" width="25%">
                        {!!element.link && element.link?.includes('linkedin') && (
                            <IconButton onClick={(e) =>  {
                                e.stopPropagation();
                                e.preventDefault();
                                window.open(element.link, '_blank', 'noopener, noreferrer');
                            }} sx={{ padding: 0 }} disableRipple>
                                <LinkedInIcon className={classes.icon} />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
                {!!element.email && (
                    <Typography className={classes.email}>
                        {element.email}
                    </Typography>
                )}
                {!!element.role && (
                    <Typography color="#666666" fontWeight={600}>
                        {element.role}
                    </Typography>
                )}
                {!!element.tags?.length && (
                    <Box my="4px">
                        <MultiSelector
                            mainOptions={element.tags}
                            selections={element.tags}
                            cropped={300}
                            readOnly />
                    </Box>
                )}
                {!!element.content ? (
                    <Stack spacing="4px" alignItems="flex-start" justifyContent="flex-start" width="100%" mt={1}>
                        <Typography color={theme.colors.neutral['500']} fontWeight={600}>
                            {'Background '}
                        </Typography>
                        <Collapse in={isExpanded} collapsedSize="fit-content"
                            sx={{ width:'100%', height: isExpanded ? '100%' : 'fit-content !important' }}>
                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" width="100%" height="100%"  >
                                <Box className={classNames(classes.collapsableText, !isExpanded ? classes.textClamp : {})}
                                    sx={{
                                        height: !isExpanded ? 'fit-content' : '200px',
                                        WebkitLineClamp: `${6 - Number(!!element.email) - Number(!!element.role) - 2*Number(!!element.tags?.length)}`,
                                        overflowY: 'auto',
                                        ...scrollbarStyle,
                                    }} onClick={(e)=>{
                                        e.preventDefault();
                                        setExpanded((prev) => !prev);
                                    }} ref={textRef}>
                                    <Typography className={classes.answer} >
                                        {element.content?.replace('- ', '• ')?.replaceAll('\n-', '\n •')}
                                    </Typography>
                                </Box>
                                <Stack direction="column" alignItems="center" justifyContent="flex-end" width={24} height="100%">
                                    <IconButton onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setExpanded((prev) => !prev);
                                        }} sx={{ paddingBottom: 'unset'}}
                                        disableRipple>
                                        <ExpandMoreIcon sx={{ width: 32, height: 32,
                                            color: theme.colors.neutral['500'],
                                            transform: `rotate(${isExpanded ? 180 : 0}deg)` }} />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Collapse>
                    </Stack>
                ) : (
                    <Stack alignItems="center" justifyContent="center" width="100%" height={130}>
                        <Typography color={theme.colors.neutral['500']} fontSize="0.9rem" fontWeight={500}>
                            {'No background summary yet.'}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Stack>
    </>);
};

const PeopleRowQueries: React.FC<{}> = () => {
    const classes = useStyles();
    const { dashboard } = useContext(DashboardContext);
    const { dashboardSource } = useBackwardsCompatible();
    const { mappedOverviewQueries, setDashboardsQueries } = useContext(DashboardsContext);
    const [keyPeopleLoaded, setKeyPeopleLoaded] = useState<boolean>(false);

    const getQueryAnswer = useCallback((title: string) => (
        mappedOverviewQueries.get(`${dashboard!.id}:${title}`)
    ), [dashboard, mappedOverviewQueries]);

    const keyPeople = useMemo(() => getQueryAnswer('Key People') ?? [], [getQueryAnswer]);

    const source = useMemo(() => {
        const dealSource = dashboardSource(dashboard!);

        return (dealSource?.name !== DashboardQuerySource.LINKEDIN) ? dealSource : undefined;
    }, [dashboard, dashboardSource]);

    const getRoleRank = useCallback((role: string): number => {
        const roleUpper = role?.toUpperCase() || '';

        // Rank 1 – Founder (ownership) / CEO (highest executive)
        if (roleUpper.includes('FOUNDER') || roleUpper.includes('CEO')) return 1;
        // Rank 2 – President (if separate from CEO)
        if (roleUpper.includes('PRESIDENT') && !roleUpper.includes('VICE')) return 2;
        // Rank 3 – All C-suite (COO, CFO, CTO, CMO, CHRO, etc.)
        if (roleUpper.includes('CHIEF') ||
            (roleUpper.includes('OFFICER') && (roleUpper.includes('CFO') || roleUpper.includes('COO') || roleUpper.includes('CTO') || roleUpper.includes('CMO') || roleUpper.includes('CPO') || roleUpper.includes('CRO') || roleUpper.includes('CHRO') || roleUpper.includes('CDO') || roleUpper.includes('CIO') || roleUpper.includes('CSO') || roleUpper.includes('CCO'))) ||
            /^C[A-Z]O\b/.test(roleUpper)) return 3;
        // Rank 4 – Executive VP / Senior VP
        if ((roleUpper.includes('EXECUTIVE') && roleUpper.includes('VP')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VP')) ||
            (roleUpper.includes('EXECUTIVE') && roleUpper.includes('VICE PRESIDENT')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VICE PRESIDENT'))) return 4;
        // Rank 5 – VP
        if (roleUpper.includes('VICE PRESIDENT') || roleUpper.includes('VP')) return 5;
        // Rank 6 – Head of / Director
        if (roleUpper.includes('HEAD OF') || roleUpper.includes('DIRECTOR')) return 6;
        // Rank 7 – Senior Managers / Senior Specialists
        if (roleUpper.includes('SENIOR')) return 7;
        // Rank 8 – Mid-level & Junior roles
        if (roleUpper.includes('MANAGER') || roleUpper.includes('LEAD') || roleUpper.includes('SPECIALIST') ||
            roleUpper.includes('ANALYST') || roleUpper.includes('COORDINATOR') || roleUpper.includes('JUNIOR')) return 8;
        // Rank 9 – Administrative & Support staff
        if (roleUpper.includes('SECRETARY') || roleUpper.includes('ASSISTANT') || roleUpper.includes('CLERK') ||
            roleUpper.includes('ADMIN') || roleUpper.includes('SUPPORT')) return 9;

        // All others (default)
        return 10;
    }, []);

    const sortedKeyPeople = useMemo(() => {
        if (Array.isArray(keyPeople)) {
            return keyPeople.sort((a: any, b: any) => {
                const rankA = getRoleRank(a.role);
                const rankB = getRoleRank(b.role);

                if (rankA !== rankB)
                    return rankA - rankB;

                if (a.role < b.role) return -1;
                if (a.role > b.role) return 1;

                return 0;
            });
        }

        return keyPeople;
    }, [getRoleRank, keyPeople]);

    useEffect(() => {
        if (!keyPeopleLoaded) {
            Promise.resolve(
                getDashboardQueriesFunc(dashboard!.id, 'Key People')
            ).then((bulkQueryData: DashboardQuery[]) => {
                setDashboardsQueries(prev => produce(prev, draft => {
                    bulkQueryData.forEach(newQuery => {
                        if (!draft.find(query => query.dashboardId === newQuery.dashboardId && query.title === newQuery.title))
                            draft.push(newQuery);
                    });
                }));
                setKeyPeopleLoaded(true);
            });
        }
    // eslint-disable-next-line
    }, [dashboard]);

    return (<>
        {keyPeopleLoaded ? (Array.isArray(sortedKeyPeople) && !!sortedKeyPeople.length ? (<>
            <Stack direction="row" spacing={4} alignItems="flex-start" justifyContent="flex-start"
                width="100%" height="100%" sx={{ overflow: 'hidden', overflowX: 'auto', ...scrollbarStyle }} p={2}>
                <DealSourceCard source={source!} />
                {sortedKeyPeople?.map((element: any, i: number) => (
                    <PersonCard element={element} key={'people-column-queries-' + i} />
                ))}
            </Stack>
        </>) : (!!source?.name) ? (
            <DealSourceCard source={source!} />
        ) : (
            <Stack spacing={2} alignItems="center" justifyContent="center" width="100%" height="100%">
                <Typography className={classes.url} color={`${theme.colors.neutral['500']} !important`}>
                    {'No key people found'}
                </Typography>
            </Stack>
        )) : (<FallbackLoading />)}
    </>);
}

export default PeopleRowQueries;